import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {BaseView} from '../../../../../core/view/base.view';
import {
  CommunicationContentModelFindAllService
} from '../../../../content-model/impl/service/communicationContentModelFindAll.service';
import {RichTextEditorComponent} from '../../../../../../components/common/rich-text-editor/rich-text-editor.component';
import {AbstractActionService} from '../../../../../core/service/v2/service/action/abstractAction.service';
import {
  WithReportFormatContentPayload
} from '../../../../../reporting/report/impl/service/payload/withReportFormatContent.payload';
import {
  CommunicationContentModelSaveDialogComponent,
  CommunicationContentModelSaveDialogInput,
  CommunicationContentModelSaveDialogResult
} from '../../../../content-model/api/component/communication-content-model-save-dialog/communication-content-model-save-dialog.component';
import {UrlParameterBuilder} from '../../../../../core/http/UrlParameterBuilder';
import {Operator, Order, Sort, UrlParameter} from '../../../../../../shared/utils/url.builder';
import {DialogController} from '../../../../../common/dialog/dialogController';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {PlatformControl} from '../../../../../../controls/core/platform/platform.control';
import {MomentAdapter} from '../../../../../../shared/utils/moment.utils';
import {
  DefaultReportActionFormatContentService
} from '../../../../../reporting/report/impl/service/formatcontent/defaultReportActionFormatContent.service';
import {
  DefaultReportFormatContentPayload
} from '../../../../../reporting/report/impl/service/formatcontent/defaultReportFormatContent.payload';
import {SelectElement} from '../../../../../../components/common/select/select.model';
import {
  LovValueSummaryFindAllService
} from '../../../../../common/lov/lov-value/impl/service/lovValueSummaryFindAll.service';
import {Subscription} from 'rxjs';
import {
  CommunicationContentSubstitutionFindAllService
} from '../../../../content-substitution/impl/service/ngrx/communicationContentSubstitutionFindAll.service';
import {
  ConsultationAiReportTextGenerationService
} from "../../../../../medical/consultation/consultation/api/service/consultationAiReportTextGeneration.service";
import {CommunicationContentEditorComponentAddTextService} from "./communicationContentEditorComponentAddText.service";
import {
  RephraseDialogComponent,
  RephraseDialogInput, RephraseDialogResult
} from "../../../../ai/api/component/rephrase-dialog/rephrase-dialog.component";
import {
  TranscriptionConfig
} from "../../../../../common/audio/transcription/api/component/audio-transcription/audio-transcription.component";
import {
  SpellCheckDialogComponent,
  SpellCheckDialogInput, SpellCheckDialogResult
} from "../../../../ai/api/component/spell-check-dialog/spell-check-dialog.component";
import {
  CommunicationContentSubstitutionModel
} from "../../../../content-substitution/api/model/communicationContentSubstitution.model";
import {LovValueSummary} from "../../../../../common/lov/lov-value/api/model/lovValueSummary.model";
import {
  AudioTranscriptionConfig
} from "../../../../../common/audio/transcription/api/component/audio-transcription-v2/audio-transcription-v2.component";
import {ChipItem} from "../../../../../core/component/chip/chip.component";

@Component({
  selector: 'app-communication-content-editor',
  templateUrl: './communication-content-editor.component.html',
  providers: [
    CommunicationContentModelFindAllService,
    DefaultReportActionFormatContentService,
    LovValueSummaryFindAllService,
    CommunicationContentSubstitutionFindAllService,
  ]
})
export class CommunicationContentEditorComponent
    extends BaseView implements OnChanges {

  SUBSTITUTIONS = 'SUBSTITUTIONS';

  @Input() parentFormGroup: FormGroup;
  @Input() parentFormControlName: string;
  @Input() initialContent;
  @Input() partnerId;
  @Input() consultationId;
  @Input() selectedCommunicationContentModelTypeValue;
  @Input() formatContentService: AbstractActionService<WithReportFormatContentPayload, any>;
  @Input() contentFormattingActivated = true;
  @Input() buildFormatContentPayload: () => WithReportFormatContentPayload;
  @Input() audioTranscriptionConfig: AudioTranscriptionConfig;
  @Input() newTextService: CommunicationContentEditorComponentAddTextService;
  @Input() substitutionAdditionalCategoryValues: string [] = [];

  @ViewChild('richTextEditorComponent') richTextEditorComponent: RichTextEditorComponent;
  typeItems: ChipItem<string> [] = [];

  formGroup: FormGroup;
  contentLoading = false;
  subscriptions: Subscription[] = [];
  newTextServiceSubscription: Subscription;
  substitutionCategories: ChipItem<CommunicationContentEditorSubstitutionCategory>[] = [];
  selectedSubstitutionCategory: CommunicationContentEditorSubstitutionCategory;
  constructor(private formBuilder: FormBuilder,
              private dialogController: DialogController,
              public communicationContentModelFindAllService: CommunicationContentModelFindAllService,
              public defaultReportActionFormatContentService: DefaultReportActionFormatContentService,
              public platformControl: PlatformControl,
              public lovValueSummaryFindAllService: LovValueSummaryFindAllService,
              public substitutionService: CommunicationContentSubstitutionFindAllService) {
    super();
    this.formGroup = this.formBuilder.group({
      typeValue: new FormControl(undefined),
      contentModelId: new FormControl(undefined),
    });
    this.subscriptions.push(this.lovValueSummaryFindAllService.entities$.subscribe((values) => {
      this.typeItems = [];
      this.typeItems.push(ChipItem.createSelectedChipItem(this.SUBSTITUTIONS, 'Expressions'));
      values.sort((a, b) => a.label.localeCompare(b.label))
          .forEach(value => this.typeItems.push(ChipItem.createChipItem(value.value, value.label)));

      //TODO selectedCommunicationContentModelTypeValue
      this.typeSelected(this.typeItems[0]);
    }));

    this.lovValueSummaryFindAllService.additionalParameters =
        [new UrlParameter('list.name', 'COMMUNICATION_CONTENT_MODEL_TYPE', Operator.EQUALS)];
    this.lovValueSummaryFindAllService.findAll();

    this.substitutionService.entities$.subscribe((values) => {
      this.substitutionCategories = [];
      values
          .filter(value => value.active)
          .forEach(value => {
          let category = this.substitutionCategories.find(value1 => value1.value.category.value === value.category.value);
          if (!category) {
            const cat = new CommunicationContentEditorSubstitutionCategory();
            cat.category = value.category;
            category = ChipItem.createChipItem(cat, value.category.label);
            this.substitutionCategories.push(category);
          }
          category.value.substitutions.push(value);
      });
      this.substitutionCategories = this.substitutionCategories.sort((a, b) => a.value.category.label.localeCompare(b.value.category.label))
      this.substitutionCategories.forEach(category => {
        category.value.substitutions = category.value.substitutions.sort((a, b) => a.name.localeCompare(b.name))
      })
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialContent']) {
      if (this.initialContent) {
        this.contentControl.setValue(undefined);
        this.contentLoading = true;
        this.formatContent(this.initialContent)
            .then(value => {
              this.richTextEditorComponent.insertString(value);
            })
            .finally(() => {
              this.contentLoading = false;
            });
      }
    }
    if (changes['newTextService']) {
      this.newTextServiceSubscription?.unsubscribe();
      if (this.newTextService) {
        this.newTextServiceSubscription = this.newTextService.$newText.subscribe(value => {
          if (value.replaceExistingContent) {
            this.contentControl.setValue(undefined);
          }
          this.contentModelChanged(value.text);
        });
      }
    }
  }

  contentModelChanged(content: string){
    if (content) {
      this.contentLoading = true;
      this.formatContent(content)
          .then(value => {
            this.richTextEditorComponent.insertString(value);
          })
          .finally(() => {
            this.contentLoading = false;
            console.log(['formGroup', this.formGroup]);
            this.formGroup.get('contentModelId').setValue(undefined);
          });
    }
  }

  selectedSubstitutionCategoryChanged(event) {
    console.log(event);
    this.selectedSubstitutionCategory = undefined;
    if (event.selected) {
      this.selectedSubstitutionCategory = event.value;
    }
  }

  typeSelected(selectedItem: ChipItem<string>) {
    if (selectedItem.selected) {
      this.formGroup.patchValue({
        typeValue: selectedItem.value
      });
    }
    this.selectedSubstitutionCategory = undefined;
    this.contentModelTypeValueChanged();
  }

  insertContent(content: string){
    this.contentModelChanged(content);
  }

  openRephraseDialog() {
    const selectedText = this.richTextEditorComponent.selectedText;
    const html = selectedText ? this.richTextEditorComponent.selectedHtml : this.richTextEditorComponent.html;
    const input: RephraseDialogInput = {
      role: 'podiatrist',
      text: html,
      audioTranscriptionHandler: this.audioTranscriptionConfig?.handler
    };
    this.dialogController.open(RephraseDialogComponent, input, {
        title: 'Reformuler un texte',
        width: '100%'
        })
        .afterClosed()
        .toPromise()
        .then((result: RephraseDialogResult) => {
            if (result) {
                if (! selectedText) {
                  this.richTextEditorComponent.clear();
                }
                this.richTextEditorComponent.insertString(result.rephrasedText);
            }
        });
  }

  openSpellCheckDialog() {
    const selectedText = this.richTextEditorComponent.selectedText;
    const html = selectedText ? this.richTextEditorComponent.selectedHtml : this.richTextEditorComponent.html;
    const input: SpellCheckDialogInput = {
      text: html,
      audioTranscriptionHandler: this.audioTranscriptionConfig?.handler
    };
    this.dialogController.open(SpellCheckDialogComponent, input, {
      title: 'Corriger un texte',
      width: '100%'
    })
        .afterClosed()
        .toPromise()
        .then((result: SpellCheckDialogResult) => {
          if (result) {
            if (! selectedText) {
              this.richTextEditorComponent.clear();
            }
            this.richTextEditorComponent.insertString(result.correctedText);
          }
        });
  }

  private formatContent(content: string): Promise<string> {
    if (!this.contentFormattingActivated) {
      return new Promise<string>((resolve) => {
        resolve(content);
      });
    }
    const customFormatContent = !! this.buildFormatContentPayload;
    const payload = customFormatContent ? this.buildFormatContentPayload() : new DefaultReportFormatContentPayload();
    payload.report = {
      content,
      partnerId: this.partnerId,
      date: payload.report?.date ? payload.report?.date : MomentAdapter.current().get()
    };
    return customFormatContent ?
        this.formatContentService.executeAction(payload) :
        this.defaultReportActionFormatContentService.executeAction(payload);
  }
  get contentControl(): FormControl {
    return this.parentFormGroup?.get(this.parentFormControlName) as FormControl;
  }

  createContentModel(initialContent?: string, id?: number) {
    const input: CommunicationContentModelSaveDialogInput = {
      initialContent,
      id
    };

    this.dialogController.open(CommunicationContentModelSaveDialogComponent, input, {
      title: id ? 'Modifier le contenu enregistré' : 'Créer un contenu enregistré',
      width: '95%'
    }).afterClosed().subscribe((result: CommunicationContentModelSaveDialogResult) => {
      if (result) {
        this.communicationContentModelFindAllService.refresh()
            .then(() => // wait to have the updated content models before setting the selected id.
                this.formGroup.get('contentModelId').setValue(result.entity.summary.id)
            );
      }
    });
  }

  contentModelTypeValueChanged() {
    const value = this.formGroup.get('typeValue')?.value;
    if (value) {
      if (value === 'SUBSTITUTIONS') {
        this.substitutionService.sort = new Sort('name', Order.ASCENDING);
        this.substitutionService.parameters = new UrlParameterBuilder().addInParameter('additionalCategory.value', this.substitutionAdditionalCategoryValues)
            .parameters;
        this.substitutionService.findAll();
      } else {
        this.communicationContentModelFindAllService.additionalParameters = new UrlParameterBuilder()
            .addParameter('type.value', value, Operator.EQUALS)
            .addParameter('active', true)
            .parameters;
        this.communicationContentModelFindAllService.sort = new Sort('label', Order.ASCENDING);
        this.communicationContentModelFindAllService.findAll();
      }
    }
  }

  drop(event: CdkDragDrop<string>) {
    const content: string = event.item.data;
    this.contentModelChanged(content);
  }
}

export class CommunicationContentEditorSubstitutionCategory {
  category: LovValueSummary;
  substitutions: CommunicationContentSubstitutionModel[] = [];
}
